import { formatRevenueCompact, formatRevenueFull } from './formatters'
import YtdLine from './YtdLine'
import QuarterProgress from './QuarterProgress'
import { DiffValue, publishedValue, type RevisionBaseline } from './DiffBits'
import { type QuarterlyMatrix, growthLabel } from './quarterlyTypes'

interface Props extends QuarterlyMatrix {
  currency?: string | null
  baseline?: RevisionBaseline | null
  fullYearColumn: boolean
}

// Bold last row of the quarterly breakdown: every column summed over channels.
export default function QuarterlySummaryRow({
  channels,
  past_quarters,
  actual_quarters,
  plan_quarters,
  past,
  plan,
  ytd,
  actual,
  actual_targets = {},
  channel_targets,
  currency,
  baseline = null,
  fullYearColumn,
}: Props) {
  const pastTotals = past_quarters.map((q) => channels.reduce((s, c) => s + Number(past[c.id]?.[q.start_date] ?? 0), 0))
  const pastGrandTotal = pastTotals.reduce((s, v) => s + v, 0)
  const actualTotals = actual_quarters.map((q) => channels.reduce((s, c) => s + Number(actual[c.id]?.[q.start_date] ?? 0), 0))
  // The summary bar needs every channel's target for the quarter.
  const actualTargetTotals = actual_quarters.map((q) => {
    const targets = channels.map((c) => actual_targets[c.id]?.[q.start_date])
    return targets.every((t) => t != null) ? targets.reduce<number>((s, t) => s + (t ?? 0), 0) : null
  })
  const grandActualTotal = actualTotals.reduce((s, v) => s + v, 0)
  const planTotals = plan_quarters.map((q) => channels.reduce((s, c) => s + (plan[c.id]?.[q.start_date] ?? 0), 0))
  const targetTotal = channels.reduce((s, c) => s + (channel_targets[c.id] ?? 0), 0)
  const ytdTotals = plan_quarters.map((q) => channels.reduce((s, c) => s + Number(ytd[c.id]?.[q.start_date] ?? 0), 0))
  const ytdGrandTotal = ytdTotals.reduce((s, v) => s + v, 0)
  const compact = (v: number) => (v > 0 ? formatRevenueCompact(v, currency) : '—')
  const fullYearTotal = fullYearColumn ? (baseline?.total_full_year ?? (grandActualTotal + targetTotal)) : targetTotal
  const growthCell = (
    <td
      className="px-3 py-2 text-right tabular-nums font-semibold"
      style={{ borderLeft: fullYearColumn ? '1px solid var(--border)' : '2px solid var(--border)', color: 'var(--foreground)' }}
    >
      <DiffValue
        was={publishedValue(baseline, (b) => b.total_target)}
        now={fullYearTotal}
        format={(x) => growthLabel(pastGrandTotal, x)}
      />
    </td>
  )

  return (
    <tr style={{ borderTop: '2px solid var(--border)' }}>
      <td className="px-3 py-2 sticky left-0 bg-background font-semibold" style={{ color: 'var(--foreground)' }}>
        Summary
      </td>
      {pastTotals.map((v, i) => (
        <td
          key={`sum-past-${past_quarters[i].start_date}`}
          className="px-3 py-2 text-center tabular-nums font-semibold"
          style={{ color: v > 0 ? 'var(--foreground)' : 'var(--muted-foreground)', borderLeft: i === 0 ? '1px solid var(--border)' : undefined }}
        >
          {compact(v)}
        </td>
      ))}
      {past_quarters.length > 0 && (
        <td
          className="px-3 py-2 text-center tabular-nums font-semibold"
          style={{ color: pastGrandTotal > 0 ? 'var(--foreground)' : 'var(--muted-foreground)', borderLeft: '1px solid var(--border)' }}
        >
          {compact(pastGrandTotal)}
        </td>
      )}
      {actualTotals.map((v, i) => (
        <td
          key={`sum-actual-${actual_quarters[i].start_date}`}
          className="px-3 py-2 text-center tabular-nums font-semibold"
          style={{ color: v > 0 ? 'var(--foreground)' : 'var(--muted-foreground)', borderLeft: i === 0 ? '2px solid var(--border)' : undefined }}
        >
          {compact(v)}
          <QuarterProgress actual={v} target={actualTargetTotals[i]} currency={currency} />
        </td>
      ))}
      {planTotals.map((v, i) => (
        <td
          key={`sum-plan-${plan_quarters[i].start_date}`}
          colSpan={2}
          className="px-3 py-2 text-right tabular-nums font-semibold"
          style={{ borderLeft: i === 0 ? '2px solid var(--border)' : undefined, color: v ? 'var(--foreground)' : 'var(--muted-foreground)' }}
        >
          {v ? formatRevenueFull(v, currency) : '—'}
          <YtdLine ytd={ytdTotals[i]} target={v} currency={currency} />
        </td>
      ))}
      {!fullYearColumn && growthCell}
      <td
        className="px-3 py-2 text-right tabular-nums font-semibold"
        style={{ borderLeft: fullYearColumn ? '2px solid var(--border)' : '1px solid var(--border)', color: 'var(--foreground)' }}
      >
        {compact(targetTotal)}
        <YtdLine ytd={ytdGrandTotal} target={targetTotal} currency={currency} />
      </td>
      {fullYearColumn && growthCell}
      {fullYearColumn && (
        <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>
          <DiffValue
            was={publishedValue(baseline, (b) => b.total_target)}
            now={fullYearTotal}
            format={(x) => formatRevenueCompact(x, currency)}
          />
        </td>
      )}
    </tr>
  )
}
